/**
 * Packaging OS — Dry-run packaging audit workflow (Phase 1P-B).
 *
 * Two-stage page:
 *   1. Form (left/top) — collects required + optional inputs that match
 *      the FastAPI Pydantic schema (PackagingAuditDryRunRequest).
 *   2. Report (right/bottom) — renders the structured DRAFT response
 *      from POST /api/workflows/packaging-audit/dry-run as 12 controlled
 *      blocks, with a halt banner for medium/high claim risk.
 *
 * Hard rules:
 *   - No live AI. The deterministic engine is on the server side.
 *   - Every output carries explicit DRAFT framing.
 *   - All 4 supported locales render correctly (en/ar/fa/tr).
 */

import { useState, type FormEvent } from "react";
import {
  DRY_RUN_CHANNELS,
  DRY_RUN_LANGUAGES,
  DRY_RUN_PACKAGE_TYPES,
  runPackagingAuditDryRun,
  type ApiResult,
  type DryRunChannel,
  type DryRunLanguage,
  type DryRunPackageType,
  type DryRunRequest,
  type DryRunResponse,
  runWorkflowPersistedFirst,
} from "../api/client";
import { Button } from "../components/Button";
import { DataState, RequestIdBadge } from "../components/DataState";
import {
  FieldGrid,
  SelectField,
  TextAreaField,
  TextField,
} from "../components/FormField";
import { BulletList, ReportSection } from "../components/ReportSection";
import { RiskPill } from "../components/RiskPill";
import { strings, type UiStrings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { AppLayout } from "./CatalogLayout";

interface FormState {
  product_name: string;
  product_category: string;
  market: string;
  channel: string;
  package_type: string;
  material_preference: string;
  claims: string;
  target_customer: string;
  language: DryRunLanguage;
  notes: string;
}

const EMPTY_FORM: FormState = {
  product_name: "",
  product_category: "",
  market: "",
  channel: "",
  package_type: "",
  material_preference: "",
  claims: "",
  target_customer: "",
  language: "en",
  notes: "",
};

type LoadState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "loaded"; result: ApiResult<DryRunResponse> };

interface FieldErrors {
  product_name?: string;
  product_category?: string;
  market?: string;
}

export function PackagingAuditWorkflowPage(): JSX.Element {
  const [locale] = useLocale();
  const t = strings(locale);
  const [form, setForm] = useState<FormState>(() => ({
    ...EMPTY_FORM,
    language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage)
      ? locale
      : "en") as DryRunLanguage,
  }));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [state, setState] = useState<LoadState>({ kind: "idle" });

  function update<K extends keyof FormState>(key: K, val: FormState[K]): void {
    setForm((prev) => ({ ...prev, [key]: val }));
  }

  function validate(): FieldErrors {
    const errs: FieldErrors = {};
    if (!form.product_name.trim()) errs.product_name = t.workflowDryRunRequiredHint;
    if (!form.product_category.trim())
      errs.product_category = t.workflowDryRunRequiredHint;
    if (!form.market.trim()) errs.market = t.workflowDryRunRequiredHint;
    return errs;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const claims = form.claims
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .slice(0, 32);

    const body: DryRunRequest = {
      product_name: form.product_name.trim(),
      product_category: form.product_category.trim(),
      market: form.market.trim(),
      language: form.language,
      claims,
    };
    if (form.channel) body.channel = form.channel as DryRunChannel;
    if (form.package_type)
      body.package_type = form.package_type as DryRunPackageType;
    if (form.material_preference.trim())
      body.material_preference = form.material_preference.trim();
    if (form.target_customer.trim())
      body.target_customer = form.target_customer.trim();
    if (form.notes.trim()) body.notes = form.notes.trim();

    setState({ kind: "submitting" });
    const result = await runWorkflowPersistedFirst("packaging-audit", body, runPackagingAuditDryRun);
    setState({ kind: "loaded", result });
  }

  function onReset(): void {
    setForm({
      ...EMPTY_FORM,
      language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage)
        ? locale
        : "en") as DryRunLanguage,
    });
    setErrors({});
    setState({ kind: "idle" });
  }

  const channelOptions = DRY_RUN_CHANNELS.map((c) => ({ value: c, label: c }));
  const packageOptions = DRY_RUN_PACKAGE_TYPES.map((p) => ({
    value: p,
    label: p,
  }));
  const languageOptions = DRY_RUN_LANGUAGES.map((l) => ({
    value: l,
    label: l.toUpperCase(),
  }));

  return (
    <AppLayout>
      <header className="po-section-header">
        <span className="po-section-header__eyebrow">
          {t.appShellNavWorkflows}
        </span>
        <h1 className="po-section-header__title">{t.workflowDryRunTitle}</h1>
        <p className="po-note">{t.workflowDryRunSubtitle}</p>
      </header>

      <section className="po-card">
        <form className="po-form" onSubmit={onSubmit} noValidate>
          <FieldGrid>
            <TextField
              label={t.workflowDryRunFieldProductName}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.product_name}
              onChange={(v) => update("product_name", v)}
              error={errors.product_name}
              maxLength={200}
            />
            <TextField
              label={t.workflowDryRunFieldProductCategory}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.product_category}
              onChange={(v) => update("product_category", v)}
              error={errors.product_category}
              maxLength={120}
            />
            <TextField
              label={t.workflowDryRunFieldMarket}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.market}
              onChange={(v) => update("market", v)}
              error={errors.market}
              maxLength={120}
            />
            <SelectField
              label={t.workflowDryRunFieldChannel}
              value={form.channel}
              onChange={(v) => update("channel", v)}
              options={channelOptions}
              emptyLabel={t.workflowDryRunNotSelected}
            />
            <SelectField
              label={t.workflowDryRunFieldPackageType}
              value={form.package_type}
              onChange={(v) => update("package_type", v)}
              options={packageOptions}
              emptyLabel={t.workflowDryRunNotSelected}
            />
            <TextField
              label={t.workflowDryRunFieldMaterial}
              value={form.material_preference}
              onChange={(v) => update("material_preference", v)}
              maxLength={120}
            />
            <TextField
              label={t.workflowDryRunFieldTargetCustomer}
              value={form.target_customer}
              onChange={(v) => update("target_customer", v)}
              maxLength={200}
            />
            <SelectField
              label={t.workflowDryRunFieldLanguage}
              value={form.language}
              onChange={(v) => update("language", v as DryRunLanguage)}
              options={languageOptions}
            />
            <TextAreaField
              label={t.workflowDryRunFieldClaims}
              hint={t.workflowDryRunFieldClaimsHelp}
              value={form.claims}
              onChange={(v) => update("claims", v)}
              rows={5}
              full
            />
            <TextAreaField
              label={t.workflowDryRunFieldNotes}
              value={form.notes}
              onChange={(v) => update("notes", v)}
              rows={3}
              maxLength={1000}
              full
            />
          </FieldGrid>

          {Object.keys(errors).length > 0 && (
            <p className="po-field__error">
              {t.workflowDryRunValidationFailed}
            </p>
          )}

          <div className="po-row">
            <Button
              variant="primary"
              type="submit"
              disabled={state.kind === "submitting"}
            >
              {state.kind === "submitting"
                ? t.workflowDryRunSubmitting
                : t.workflowDryRunSubmit}
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={onReset}
              disabled={state.kind === "submitting"}
            >
              {t.workflowDryRunReset}
            </Button>
          </div>
        </form>
      </section>

      {state.kind === "loaded" && (
        <DataState
          state={{ kind: "loaded", result: state.result }}
          t={t}
          renderOk={(data, requestId) => (
            <DryRunReport data={data} requestId={requestId} t={t} />
          )}
        />
      )}
    </AppLayout>
  );
}

interface DryRunReportProps {
  data: DryRunResponse;
  requestId: string | null;
  t: UiStrings;
}

function DryRunReport({ data, requestId, t }: DryRunReportProps): JSX.Element {
  const risk = data.claim_risk;
  const riskLabel =
    risk.risk_level === "high"
      ? t.reportRiskHigh
      : risk.risk_level === "medium"
        ? t.reportRiskMedium
        : t.reportRiskLow;

  return (
    <div className="po-stack">
      {risk.risk_level === "high" && (
        <div className="po-halt-banner po-halt-banner--high" role="alert">
          {t.reportHaltBannerHigh}
        </div>
      )}
      {risk.risk_level === "medium" && (
        <div className="po-halt-banner po-halt-banner--medium" role="alert">
          {t.reportHaltBannerMedium}
        </div>
      )}

      <ReportSection title={t.reportInputSummary}>
        <dl className="po-dl">
          <dt>{t.workflowDryRunFieldProductName}</dt>
          <dd>{data.input_summary.product_name}</dd>
          <dt>{t.workflowDryRunFieldProductCategory}</dt>
          <dd>{data.input_summary.product_category}</dd>
          <dt>{t.workflowDryRunFieldMarket}</dt>
          <dd>{data.input_summary.market}</dd>
          <dt>{t.workflowDryRunFieldChannel}</dt>
          <dd>
            <span className="dir-ltr-island">
              {data.input_summary.channel ?? "—"}
            </span>
          </dd>
          <dt>{t.workflowDryRunFieldPackageType}</dt>
          <dd>
            <span className="dir-ltr-island">
              {data.input_summary.package_type ?? "—"}
            </span>
          </dd>
          <dt>{t.workflowDryRunFieldMaterial}</dt>
          <dd>{data.input_summary.material_preference ?? "—"}</dd>
          <dt>{t.workflowDryRunFieldClaims}</dt>
          <dd>
            <span className="po-table__cell-id">
              {data.input_summary.claims_count}
            </span>
          </dd>
          <dt>{t.workflowDryRunFieldLanguage}</dt>
          <dd>
            <span className="dir-ltr-island">
              {data.input_summary.language.toUpperCase()}
            </span>
          </dd>
        </dl>
      </ReportSection>

      <ReportSection title={t.reportPackagingAudit}>
        <h3 className="po-report-section__subtitle">
          {t.reportPackagingAuditPositioning}
        </h3>
        <BulletList items={data.packaging_audit.positioning_notes} />
        <h3 className="po-report-section__subtitle">
          {t.reportPackagingAuditPackStructure}
        </h3>
        <BulletList items={data.packaging_audit.pack_structure_notes} />
        <h3 className="po-report-section__subtitle">
          {t.reportPackagingAuditShelf}
        </h3>
        <BulletList
          items={data.packaging_audit.shelf_and_delivery_considerations}
        />
      </ReportSection>

      <ReportSection title={t.reportLabelCopy}>
        <h3 className="po-report-section__subtitle">{t.reportLabelCopyFront}</h3>
        <BulletList items={data.label_copy_suggestions.front_of_pack} />
        <h3 className="po-report-section__subtitle">{t.reportLabelCopySide}</h3>
        <BulletList items={data.label_copy_suggestions.side_panel} />
        <h3 className="po-report-section__subtitle">{t.reportLabelCopyTone}</h3>
        <BulletList items={data.label_copy_suggestions.tone_notes} />
      </ReportSection>

      <ReportSection title={t.reportMaterialNotes}>
        <BulletList items={data.material_notes} />
      </ReportSection>

      <ReportSection title={t.reportClaimRisk}>
        <div className="po-row">
          <span className="po-note">{t.reportClaimRiskLevelLabel}:</span>
          <RiskPill level={risk.risk_level} label={riskLabel} />
          <span className="po-note">
            {t.reportClaimRiskReviewRequired}:{" "}
            {risk.review_required
              ? t.reportReviewRequiredYes
              : t.reportReviewRequiredNo}
          </span>
        </div>
        <h3 className="po-report-section__subtitle">
          {t.reportClaimRiskFlags}
        </h3>
        <BulletList items={risk.flags} empty={t.reportNoFlags} />
        {risk.review_reasons.length > 0 && (
          <>
            <h3 className="po-report-section__subtitle">
              {t.reportClaimRiskReviewReasons}
            </h3>
            <BulletList items={risk.review_reasons} />
          </>
        )}
      </ReportSection>

      <ReportSection title={t.reportSupplierBrief}>
        <BulletList items={data.supplier_brief_outline} />
      </ReportSection>

      <ReportSection title={t.reportLaunchKit}>
        <BulletList items={data.launch_kit_outline} />
      </ReportSection>

      <ReportSection title={t.reportTrustNotes}>
        <dl className="po-dl">
          <dt>{t.trustNotesSourceBacked}</dt>
          <dd>
            {data.trust_notes.source_backed
              ? t.reportReviewRequiredYes
              : t.reportReviewRequiredNo}
          </dd>
          <dt>{t.trustNotesRegulatoryGuarantee}</dt>
          <dd>
            {data.trust_notes.regulatory_guarantee
              ? t.reportReviewRequiredYes
              : t.reportReviewRequiredNo}
          </dd>
          <dt>{t.trustNotesFinalOutput}</dt>
          <dd>
            {data.trust_notes.final_output
              ? t.reportReviewRequiredYes
              : t.reportReviewRequiredNo}
          </dd>
          <dt>{t.reportClaimRiskReviewRequired}</dt>
          <dd>
            {data.trust_notes.human_review_required
              ? t.reportReviewRequiredYes
              : t.reportReviewRequiredNo}
          </dd>
        </dl>
      </ReportSection>

      <ReportSection title={t.reportNextSteps}>
        <BulletList items={data.next_steps} />
      </ReportSection>

      {requestId && <RequestIdBadge t={t} requestId={requestId} />}
    </div>
  );
}
